/**
 * Provides utility functions for form validation and submission in a React application context.
 * These functions allow for the dynamic validation of form fields based on configurable rules,
 * and the submission of form data to a specified API endpoint.
 *
 */
import axios from 'axios';
import { getApiConfig, getApiUri } from '../configUtils';
import { matchIsValidTel } from 'mui-tel-input';
import isEmail from 'validator/lib/isEmail';

/**
 * Validates all fields in a form state against a set of provided validation rules.
 * Each rule specifies a validation function that determines the validity of the field's value
 * and, optionally, additional arguments required for validation (e.g. country code).
 *
 * @param {Object} currentState - The current state of the form fields
 * @param {Function} updateStateFn - A function to update the form field state
 * @param {Object} validationRules - An object containing validation rules for the form fields
 * @returns {boolean} True if all fields are valid, false if any validation fails
 */
export const validateFields = (
    currentState,
    updateStateFn,
    validationRules
) => {
    let validationFailed = false;

    Object.entries(validationRules).forEach(
        ([key, { validate, additionalValidationArguments }]) => {
            // Check if the current form state has the key to validate
            if (currentState[key]) {
                let validationArguments = [currentState[key].value];

                //Check if the key we are validating has any other additional fields that are required for validation
                if (additionalValidationArguments) {
                    additionalValidationArguments.forEach((argKey) => {
                        validationArguments.push(currentState[key][argKey]);
                    });
                }
                const isValid = validate(...validationArguments);

                if (!isValid) {
                    validationFailed = true;
                    // Dynamically call the update function for the specific form field
                    updateStateFn(key, { valid: false, error: true });
                } else {
                    // Ensure fields previously in error are reset
                    updateStateFn(key, { valid: true, error: false });
                }
            } else {
                console.warn(
                    `Validation rule exists for '${key}' but the formState does not have this key.`
                );
            }
        }
    );
    return !validationFailed;
};

/**
 * This function iterates over each field in the provided form state object. It checks if the value of
 * the field is a non-empty string (after trimming white spaces) for general fields. For the phoneNumber
 * field, it additionally ensures the value is not just the country code prefixed with a '+'. Only fields
 * that meet these criteria are included in the returned object.
 *
 * @param {Object} formState - The form state object containing field values to be filtered.
 * @returns {Object} A new object containing only the fields from the input form state object that are
 *                   non-empty strings and, in the case of phoneNumber, not just the country code.
 */
export const filterEmptyFields = (formState) => {
    return Object.keys(formState).reduce((filteredFields, key) => {
        const { value, countryCode } = formState[key];
        const isPhoneNumber = key === 'phoneNumber';
        const isNonEmpty = value.trim() !== '';
        const isValidPhoneNumber = isPhoneNumber
            ? value !== `+${countryCode}`
            : true;

        if (isNonEmpty && isValidPhoneNumber) {
            filteredFields[key] = value;
        }

        return filteredFields;
    }, {});
};

/**
 * Generate user action form submission configuration including function to construct api payload
 * @param {String} actionType - User action type
 * @param {Object} userDetails - Signed-in user details
 * @returns {{constructFormPayloadFields: (function(*): {action: *, userEmail: *, fields: {}, userId: *}), apiUrlSuffix: string, httpMethod: string}}
 */
export const getUserActionSubmitConfig = (actionType, userDetails) => {
    return {
        apiUrlSuffix: 'user_action',
        constructFormPayloadFields: (formState) => ({
            userId: userDetails.auth0Details.sub,
            userEmail: userDetails.auth0Details.email,
            fields: filterEmptyFields(formState),
            action: actionType
        }),
        httpMethod: 'post'
    };
};

/**
 * Validates a phone number using the matchIsValidTel function from 'mui-tel-input'.
 * Allows for a value with only the country code to be considered valid.
 * We do this because the country code cannot be removed from the MuiTelInput
 *
 * @param {string} value - The phone number value to validate
 * @param {string} countryCode - The country code of the phone number without the `+`
 * @returns {boolean} True if the phone number is valid or empty, false otherwise
 */
export const validatePhoneNumber = (value, countryCode) => {
    const isJustCountryCode = value === '+' + countryCode;

    return matchIsValidTel(value) || isJustCountryCode;
};

/**
 * Validates if the provided value is an empty string or a valid email address (using isEmail from the validator package).
 * @param {string} value - The value to be validated as an email address.
 * @returns {boolean} - Returns true if the value is an empty string or matches the email format, false otherwise.
 */
export const validateEmailAddress = (value) => {
    return value === '' || isEmail(value);
};

/**
 * Submits form data to a configured API endpoint. Constructs the request payload using
 * form state and configuration, then performs a POST request with Axios.
 *
 * @param {Object} formState - The current state of the form fields
 * @param {Object} config - Configuration for the submission, including API endpoint and action
 * @param {string} token - Authorization token for the API request
 */
export async function onSubmit(formState, config, token) {
    const { apiUrlSuffix, constructFormPayloadFields, httpMethod } = config;
    const apiUrl = getApiUri(getApiConfig()) + apiUrlSuffix;

    const payload = { ...constructFormPayloadFields(formState) };

    await axios({
        method: httpMethod,
        url: apiUrl,
        data: payload,
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
}
